import { FC, useCallback, useMemo } from 'react';

import SectionContainer from '@common/components/SectionContainer';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useCheckForArrivalDate } from '@common/hooks';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useSpaSearchComplete } from '@features/JourneyPickerV2/useSpaSearchComplete';
import { useTranslations } from '@lang';
import { useRouter } from 'shared-routing';

import { useGetSpaCategories } from '@features/spa/hooks/useGetSpaCategories';

import { SpaCategoryListTemplate, SpaListTemplate } from 'voyager';

import { useGetSpaSettings } from '@features/spa/hooks/useGetSpaSettings';
import { mapCategoriesToCards } from '@features/spa/utils/mapCategoriesToCards';

import { useGetAllSpaPackages } from '@features/spa/hooks/useGetAllSpaPackages';
import { useGetSpaDaysPage } from '@features/spa/hooks/useGetSpaDaysPage';
import { useSpaEntityFilter } from '@features/spa/hooks/useSpaEntityFilter';
import { buildCategoryOptions } from '@features/spa/utils/buildCategoryOptions';
import { useSpaDaysList } from './hooks/useSpaDaysList';
import { SpaDaysV2Props } from './SpaDaysV2.types';

export const SpaDaysV2: FC<SpaDaysV2Props> = ({
  property,
  journeyParams,
  categoryId = null,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.SPA_DAY_LIST,
  });

  const filter = useSpaEntityFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
    guests: journeyParams?.spaAdults,
  });

  const handleSearchComplete = useSpaSearchComplete();

  const { listItems, allListItems, isError, isInitialLoading, isRefetching } = useSpaDaysList({
    propertyId: property.id,
    language,
    categoryFilterId: filter.categoryFilterId,
    categoryFilterValue: filter.categoryFilterValue,
    date: filter.appliedDateString,
    guests: filter.guests,
  });

  const { data: spaPackagesCategories = [], isFetching: isFetchingCategories } =
    useGetSpaCategories(property.id, 'packages', language);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const isSpaDayCategoryViewVisible = spaSettings?.isSpaDayCategoryViewVisible ?? false;
  const showCategoryView = isSpaDayCategoryViewVisible && spaPackagesCategories.length > 1;

  const { data: allSpaPackages = [] } = useGetAllSpaPackages(property.id, language);

  const { data: spaDaysPage } = useGetSpaDaysPage(property.id, language);
  const fallbackImageUrl = spaDaysPage?.heroBackgroundImage?.url;

  const uncategorizedItems = useMemo(
    () => allSpaPackages.filter((item) => !item.categories || item.categories.length === 0),
    [allSpaPackages]
  );

  const noCategoryLabel = t('spa.noCategory');

  const categoryOptions = buildCategoryOptions(
    spaPackagesCategories,
    t('spa.package.allCategories'),
    uncategorizedItems.length > 0 ? noCategoryLabel : undefined
  );

  const handleSelectSpaDay = (id: string) => {
    router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id } });
  };

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const handleSelectCategory = (id: string) => {
    router.push({ pathname: Routes.SPA_DAY_CATEGORY_DETAIL, query: { id } });
  };

  const titleLabel = spaDaysPage?.title || t('category.spaDays');

  const DURATION_ORDER = ['full_day', 'half_day', 'evening'];

  const durationOptions = useMemo(() => {
    const available = new Set(
      allListItems.map((item) => item.durationValue).filter(Boolean) as string[]
    );
    const ordered = DURATION_ORDER.filter((value) => available.has(value));
    const rest = [...available].filter((value) => !DURATION_ORDER.includes(value));
    return [...ordered, ...rest].map((value) => ({
      label: t(value as Parameters<typeof t>[0]),
      value,
    }));
  }, [allListItems, t]);

  return (
    <EntitiesListingWrapper>
      {!showCategoryView && (
        <JourneyPickerOuterWrapper>
          <JourneyPickerInnerWrapper>
            <JourneyPickerV2
              dateMode="single"
              dateContext="spa"
              showGuests
              categories={navCategories}
              category="spa-days"
              onCategoryChange={handleNavCategoryChange}
              onSearchComplete={handleSearchComplete}
            />
          </JourneyPickerInnerWrapper>
        </JourneyPickerOuterWrapper>
      )}
      <SectionContainer>
        {showCategoryView ? (
          <SpaCategoryListTemplate
            categories={mapCategoriesToCards(
              spaPackagesCategories,
              'spaPackages',
              uncategorizedItems.length > 0
                ? { label: noCategoryLabel, imageUrl: fallbackImageUrl }
                : undefined
            )}
            onSelectCategory={handleSelectCategory}
            isLoading={isFetchingCategories}
            titleLabel={titleLabel}
            fromPriceLabel={t('common.from')}
            noResultsLabel={t('spa.noResults')}
            noImageLabel={t('spa.noImage')}
            columns={2}
            mobileColumns={2}
          />
        ) : (
          <>
            <Spinner showDelayMessage isActive={isRefetching} />
            <SpaListTemplate
              items={listItems}
              onSelectItem={handleSelectSpaDay}
              isLoading={isInitialLoading}
              titleLabel={titleLabel}
              fromPriceLabel={t('common.from')}
              soldOutLabel={t('common.unavailableForSearch')}
              editSearchLabel={t('common.editSearch')}
              onEditSearch={handleEditSearch}
              noResultsLabel={isError ? t('spa.integrationUnavailable') : t('spa.noResults')}
              noImageLabel={t('spa.noImage')}
              columns={2}
              formatGuests={(min, max) =>
                min === max
                  ? t('common.numberOfGuests', { guestsAmount: min })
                  : t('common.guestRange', { from: min, to: max })
              }
              durationOptions={durationOptions}
              categoryOptions={categoryOptions}
              selectedCategory={filter.selectedCategory || ''}
              onCategoryChange={filter.handleDirectCategoryChange}
              categoryLabel={t('common.category')}
            />
          </>
        )}
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
